<template>
  <div class="bottom-panel-padding">
    <BaseHelpTooltip
      class="d-flex"
      options
    >
      <template slot="left">
        <h3>Article Trackers</h3>
      </template>
      <template slot="popover-body">
        <p>
          We support the following pixel tracker domains:
        </p>
        <ul class="bg-light p-3">
          <li>Sizmek : https://bs.serving-sys.com</li>
          <li>Google Ad Manager : https://ad.doubleclick.net</li>
          <li>Atlas Solutions : https://ad.atdmt.com</li>
          <li>Flashtalking : http://servedby.flashtalking.com</li>
        </ul>
        <p>
          Please use one of these for your impression and click tracking requirements.<br>

          We have no restrictions on post click trackers. Please add the string provided
          by your chosen tool and SmartMatch(TM) will append this to your Call To Action
          URL when your adverts are served.
        </p>
      </template>
      <template slot="options-body">
        <b-button
          :class="{ 'btn-loading': isLoading }"
          :disabled="isNotValid || this.isCampaignReadonly"
          @click="submitTrackers"
          data-test="tracker-submit-button"
          variant="primary"
        >
          Save
        </b-button>
      </template>
    </BaseHelpTooltip>
    <b-form-group
      id="fieldset-1"
      class="form-label"
      label="Impression Tracker"
    >
      <b-form-textarea
        :state="impressionValid"
        autocomplete="off"
        class="form-control"
        data-test="content-tracker-impression"
        id="input-1"
        placeholder="Enter a URL"
        trim
        type="url"
        v-model="impression"
        :disabled="this.isCampaignReadonly"
      />
    </b-form-group>
    <b-form-group
      id="fieldset-2"
      class="form-label"
      label="Click Tracker"
    >
      <b-form-textarea
        :state="clickValid"
        autocomplete="off"
        class="form-control"
        data-test="content-tracker-click"
        id="input-2"
        pattern="https?://.+"
        placeholder="Enter a URL"
        trim
        type="url"
        v-model="click"
        :disabled="this.isCampaignReadonly"
      />
    </b-form-group>
    <b-form-group
      id="fieldset-3"
      class="form-label"
      label="Post click Tracker"
    >
      <b-form-textarea
        :state="postClickValid"
        autocomplete="off"
        class="form-control"
        data-test="content-tracker-post-click"
        id="input-3"
        placeholder="Enter a post-click tracker"
        trim
        type="url"
        v-model="postClick"
        :disabled="this.isCampaignReadonly"
      />
    </b-form-group>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import * as trackerTypes from '@/modules/trackers/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';


import helper from '@/core/helper';

export default {
  name: 'TrackerPanel',
  data() {
    return {
      isLoading: false,
      impression: '',
      click: '',
      postClick: '',
      impressionValid: null,
      clickValid: null,
      postClickValid: null,
    };
  },
  props: {
    encodedExternalId: {
      type: String,
      default: '',
    },
    readOnly: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState('trackers', [
      'contentTrackers',
    ]),
    ...mapState('campaigns', [
      'selectedAdvertiser',
    ]),
    ...mapGetters('campaigns', {
      isCampaignReadonly: campaignTypes.GET_IS_CAMPAIGN_READONLY,
    }),
    ...mapGetters('content', {
      creative: contentTypes.GET_CURRENT_SELECTED_CREATIVE,
    }),
    isClickValid() {
      return (this.click === '')
        ? null : !!(helper.validURL(this.click));
    },
    getTrackersForContent() {
      const content = this.contentTrackers
        .find((item) => item.contentItemUuid === this.encodedExternalId);
      if (typeof (content) === 'undefined') return [];
      return content;
    },
    isNotValid() {
      if (this.isLoading) return true;
      return this.impression === '' && (this.isClickValid === null || !this.isClickValid) && this.postClick === '';
    },
  },
  methods: {
    ...mapActions('trackers', [
      trackerTypes.SAVE_SELF_SERVICE_CONTENT_TRACKERS,
    ]),
    submitTrackers() {
      this.isLoading = true;

      const trackersPayload = {
        advertisersId: this.selectedAdvertiser.id,
        body: {
          creativeId: this.creative.id,
          contentItemUuid: this.encodedExternalId,
          impressionTracker: this.impression,
          clickTracker: this.click,
          postClickTracker: this.postClick,
        },
      };

      if (this.impression || this.click || this.postClick) {
        this.SAVE_SELF_SERVICE_CONTENT_TRACKERS(trackersPayload).then(() => {
          this.isLoading = false;
        }).catch(() => {
          this.isLoading = false;
        });
      } else {
        this.isLoading = false;
      }
    },
    updateTrackers() {
      const { impressionTracker, clickTracker, postClickTracker } = this.getTrackersForContent;
      this.impression = impressionTracker?.value;
      this.click = clickTracker?.value;
      this.postClick = postClickTracker?.value;

      this.impressionValid = impressionTracker?.valid;
      this.clickValid = clickTracker?.valid;
      this.postClickValid = postClickTracker?.valid;
    },
    getShowWarning(tracker) {
      return (!tracker || !tracker.value || tracker.valid === undefined) ? false : !tracker.valid;
    },
  },
  mounted() {
    this.updateTrackers();
  },
  watch: {
    getTrackersForContent() {
      this.updateTrackers();
    },
    creative() {
      this.updateTrackers();
    },
  },
};
</script>
